import { type PrimaryKeyAI } from '@myplayer/gs1';
import {
  IdentifierDraftSchema,
  TargetLinkSchema,
  type ConditionalRoutingRule,
  type DraftTargetLink,
  type IdentifierRecord,
  type SectorId,
  type StagedDraftRecord,
} from '@myplayer/schema';
import { validateIdentifierCandidate } from '@myplayer/validation';
import { type AdminUserSession } from './auth-context.js';
import { getFirebaseClients } from './firebase.js';
import {
  checkAdminIdentifierUniqueness,
  createStagedIdentifierInFirestore,
  formatPlainValidationError,
  saveStagedDraftLinksInFirestore,
  validateGs1KeyInput,
} from './products-firestore.js';
import { resolveScheduledChange } from './products-view-helpers.js';

export {
  CROSS_ORG_GTIN_CONFLICT_MESSAGE,
  buildRecordConflictKey,
  buildSameOrgGtinConflictMessage,
  checkAdminIdentifierUniqueness,
  checkFirestoreOrgIdentifierUniqueness,
  computeDuplicateIdentifierConflicts,
  getDuplicateConflictForRecord,
  parseRecordCreatedAtMs,
  type DuplicateIdentifierConflict,
} from './products-firestore.js';
export * from './products-recall-helpers.js';
export * from './products-view-helpers.js';

export async function createProductDraftAction(params: {
  readonly session: AdminUserSession | null;
  readonly targetWorkspaceId?: string | undefined;
  readonly newAi: PrimaryKeyAI;
  readonly newKeyInput: string;
  readonly newTitle: string;
  readonly newBrand: string;
  readonly newDefaultUrl: string;
  readonly isControlled: boolean;
  readonly existingRecords?: readonly IdentifierRecord[] | undefined;
  readonly knownWorkspaceIds?: readonly string[] | undefined;
  readonly workspaceNameMap?: ReadonlyMap<string, string> | undefined;
  readonly setNewFormError: (err: string | null) => void;
  readonly setCreatingProduct: (v: boolean) => void;
  readonly onCreated: (identifierKey: string, localRecord?: IdentifierRecord) => void;
}): Promise<void> {
  if (!params.session) {
    params.setNewFormError('You must be signed in to add a product.');
    return;
  }
  const effectiveWorkspaceId =
    params.targetWorkspaceId && params.targetWorkspaceId.trim().length > 0
      ? params.targetWorkspaceId.trim()
      : params.session.activeWorkspaceId;
  const keyValidation = validateGs1KeyInput(params.newAi, params.newKeyInput);
  if (!keyValidation.ok) {
    params.setNewFormError(
      formatPlainValidationError({
        code: keyValidation.errorCode,
        message: keyValidation.message,
      }),
    );
    return;
  }
  const conflictMessage = checkAdminIdentifierUniqueness({
    identifierKey: keyValidation.identifierKey,
    orgId: params.session.orgId,
    workspaceId: effectiveWorkspaceId,
    existingRecords: params.existingRecords,
    workspaceNameMap: params.workspaceNameMap,
  });
  if (conflictMessage !== null) {
    params.setNewFormError(conflictMessage);
    return;
  }
  if (params.newTitle.trim().length === 0) {
    params.setNewFormError('Please enter a product name.');
    return;
  }
  const initialLinkCandidate: DraftTargetLink = {
    linkType: 'gs1:pip',
    href: params.newDefaultUrl.trim(),
    title: params.newTitle.trim(),
    type: 'text/html',
    hreflang: ['en'],
    context: ['GLOBAL'],
    defaultLink: true,
  };
  const linkSchemaResult = TargetLinkSchema.safeParse(initialLinkCandidate);
  if (!linkSchemaResult.success) {
    params.setNewFormError(
      'Please enter a valid web address starting with https:// (for example, https://example.com/product).',
    );
    return;
  }
  const candidateCheck = validateIdentifierCandidate(
    keyValidation.ai,
    keyValidation.canonicalPrimaryKey,
    params.newDefaultUrl.trim(),
    [initialLinkCandidate],
    { requireSingleDefaultLink: true },
  );
  if (candidateCheck.status === 'INVALID') {
    const firstErr = candidateCheck.errors[0];
    params.setNewFormError(
      firstErr
        ? formatPlainValidationError(firstErr)
        : 'Please check the product barcode and web address.',
    );
    return;
  }

  params.setCreatingProduct(true);
  try {
    const nowIso = new Date().toISOString();
    const stagedDraft: StagedDraftRecord = {
      title: params.newTitle.trim(),
      ...(params.newBrand.trim().length > 0 ? { brand: params.newBrand.trim() } : {}),
      status: 'ACTIVE',
      defaultUrl: params.newDefaultUrl.trim(),
      links: [initialLinkCandidate],
      rules: [],
      submittedBy: params.session.uid,
      submittedAt: nowIso,
    };
    if (params.isControlled) {
      const createdRecord: IdentifierRecord = {
        identifierKey: keyValidation.identifierKey,
        orgId: params.session.orgId,
        workspaceId: effectiveWorkspaceId,
        ai: keyValidation.ai,
        primaryKey: keyValidation.canonicalPrimaryKey,
        sector: 'RETAIL_CPG',
        title: params.newTitle.trim(),
        ...(params.newBrand.trim().length > 0 ? { brand: params.newBrand.trim() } : {}),
        status: 'ACTIVE',
        version: 1,
        defaultUrl: params.newDefaultUrl.trim(),
        links: [initialLinkCandidate],
        rules: [],
        validation: {
          status: 'VALID',
          validatedAt: nowIso,
          errors: [],
          warnings: [],
        },
        createdBy: params.session.uid,
        updatedBy: params.session.uid,
      };
      params.onCreated(createdRecord.identifierKey, createdRecord);
    } else {
      const { db } = getFirebaseClients();
      const result = await createStagedIdentifierInFirestore({
        db,
        orgId: params.session.orgId,
        workspaceId: effectiveWorkspaceId,
        uid: params.session.uid,
        ai: params.newAi,
        primaryKey: params.newKeyInput,
        title: params.newTitle,
        ...(params.newBrand.trim().length > 0 ? { brand: params.newBrand.trim() } : {}),
        defaultUrl: params.newDefaultUrl,
        links: [initialLinkCandidate],
        knownWorkspaceIds: params.knownWorkspaceIds,
        workspaceNameMap: params.workspaceNameMap,
      });
      const stagedRecord: IdentifierRecord = {
        identifierKey: result.identifierKey,
        orgId: params.session.orgId,
        workspaceId: effectiveWorkspaceId,
        ai: keyValidation.ai,
        primaryKey: keyValidation.canonicalPrimaryKey,
        sector: 'RETAIL_CPG',
        status: 'DRAFT',
        createdBy: params.session.uid,
        updatedBy: params.session.uid,
        draft: stagedDraft,
      };
      params.onCreated(result.identifierKey, stagedRecord);
    }
  } catch (err: unknown) {
    params.setNewFormError(
      err instanceof Error
        ? formatPlainValidationError({ code: 'CREATE_ERROR', message: err.message })
        : 'Could not save the product right now. Please try again.',
    );
  } finally {
    params.setCreatingProduct(false);
  }
}

export async function stageDraftLinksAction(params: {
  readonly session: AdminUserSession | null;
  readonly selectedRecord: IdentifierRecord | null;
  readonly nextLinks: readonly DraftTargetLink[];
  readonly nextRules?: readonly ConditionalRoutingRule[];
  readonly nextSector?: SectorId;
  readonly isControlled: boolean;
  readonly setLinkFormError: (err: string | null) => void;
  readonly setLinkFormSuccess: (msg: string | null) => void;
  readonly setSavingDraft: (v: boolean) => void;
  readonly onStageDraftLinks?:
    | ((params: {
        readonly identifierKey: string;
        readonly draft: StagedDraftRecord;
        readonly sector?: SectorId | undefined;
      }) => Promise<IdentifierRecord | void> | IdentifierRecord | void)
    | undefined;
  readonly onLocalDraftUpdated: (
    identifierKey: string,
    nextDraft: StagedDraftRecord,
    uid: string,
    nextSector?: SectorId,
    stagedOverride?: IdentifierRecord,
  ) => void;
  readonly onPendingPromotionStarted?: (
    identifierKey: string,
    scheduledMessage: string | null,
  ) => void;
}): Promise<boolean> {
  if (!params.session || !params.selectedRecord) {
    params.setLinkFormError('No product selected.');
    return false;
  }
  const activeLinks = params.nextLinks.filter((l) => l.removed !== true);
  if (activeLinks.length === 0) {
    params.setLinkFormError('Please keep at least one destination web address for this product.');
    return false;
  }
  const hasDefault = activeLinks.some((l) => l.defaultLink);
  const normalizedLinks: DraftTargetLink[] = params.nextLinks.map((link, idx) => {
    if (link.removed === true) return { ...link, defaultLink: false };
    if (!hasDefault) {
      const firstActiveIdx = params.nextLinks.findIndex((item) => item.removed !== true);
      return { ...link, defaultLink: idx === firstActiveIdx };
    }
    return link;
  });

  const rec = params.selectedRecord;
  const effectiveTitle = rec.draft?.title ?? rec.title ?? rec.primaryKey;
  const effectiveBrand = rec.draft?.brand ?? rec.brand;
  const effectiveDefaultUrl =
    normalizedLinks.find((l) => l.removed !== true && l.defaultLink)?.href ??
    rec.draft?.defaultUrl ??
    rec.defaultUrl ??
    'https://brand.example.com';
  const effectiveRules = params.nextRules ?? rec.draft?.rules ?? rec.rules ?? [];
  const nextDraft: StagedDraftRecord = {
    title: effectiveTitle,
    ...(effectiveBrand !== undefined ? { brand: effectiveBrand } : {}),
    status: rec.draft?.status ?? rec.status ?? 'ACTIVE',
    defaultUrl: effectiveDefaultUrl,
    links: normalizedLinks,
    rules: effectiveRules,
    submittedBy: params.session.uid,
    submittedAt: new Date().toISOString(),
  };

  params.setSavingDraft(true);
  try {
    const scheduled = resolveScheduledChange({
      ...rec,
      draft: nextDraft,
    });
    const promotedSuccessMessage =
      scheduled !== null
        ? `Saved — Scheduled for ${scheduled.formattedDate} · Changes to ${scheduled.targetHref} on ${scheduled.formattedDate}`
        : 'Saved — Live now';

    if (params.isControlled) {
      for (const link of normalizedLinks) {
        const parsedLink = TargetLinkSchema.safeParse(link);
        if (!parsedLink.success) {
          throw new Error(
            'Please enter a valid web address starting with https:// for every link.',
          );
        }
      }
      const normalizedZodRules = effectiveRules.map((r) => ({
        id: r.id,
        priority: r.priority,
        enabled: (r as { readonly active?: boolean }).active !== false,
        condition: r.condition,
        targetHref: r.targetHref,
        linkType: r.linkType,
      }));
      const parsedDraft = IdentifierDraftSchema.safeParse({
        ...nextDraft,
        rules: normalizedZodRules,
      });
      if (!parsedDraft.success) {
        throw new Error(
          parsedDraft.error.issues[0]?.message ?? 'Please check your link details and try again.',
        );
      }
      const valBlock = validateIdentifierCandidate(
        rec.ai,
        rec.primaryKey,
        effectiveDefaultUrl,
        normalizedLinks.filter((l) => l.removed !== true),
        { requireSingleDefaultLink: true },
        effectiveRules,
      );
      if (valBlock.status === 'INVALID') {
        const firstErr = valBlock.errors[0];
        throw new Error(
          firstErr
            ? formatPlainValidationError(firstErr)
            : 'Please check your destination web address and try again.',
        );
      }
      let customRecord: IdentifierRecord | void = undefined;
      if (params.onStageDraftLinks !== undefined) {
        customRecord = await params.onStageDraftLinks({
          identifierKey: rec.identifierKey,
          draft: nextDraft,
          ...(params.nextSector !== undefined ? { sector: params.nextSector } : {}),
        });
      }
      params.onLocalDraftUpdated(
        rec.identifierKey,
        nextDraft,
        params.session.uid,
        params.nextSector,
        customRecord ?? undefined,
      );
      const isAwaitingPromotion =
        params.onStageDraftLinks !== undefined &&
        (customRecord === undefined ||
          (customRecord.draft !== undefined && customRecord.validation?.status !== 'INVALID'));
      if (isAwaitingPromotion) {
        params.onPendingPromotionStarted?.(
          rec.identifierKey,
          scheduled !== null ? promotedSuccessMessage : null,
        );
        params.setLinkFormSuccess('Checking…');
      } else if (customRecord?.validation?.status === 'INVALID') {
        params.setLinkFormSuccess(null);
      } else {
        params.setLinkFormSuccess(promotedSuccessMessage);
      }
    } else {
      const { db } = getFirebaseClients();
      await saveStagedDraftLinksInFirestore({
        db,
        orgId: params.session.orgId,
        workspaceId: rec.workspaceId || params.session.activeWorkspaceId,
        identifierKey: rec.identifierKey,
        uid: params.session.uid,
        ai: rec.ai,
        primaryKey: rec.primaryKey,
        title: effectiveTitle,
        ...(effectiveBrand !== undefined ? { brand: effectiveBrand } : {}),
        status: rec.draft?.status ?? rec.status ?? 'ACTIVE',
        defaultUrl: effectiveDefaultUrl,
        links: normalizedLinks,
        rules: effectiveRules,
      });
      const stagedOnlyRecord: IdentifierRecord = {
        ...rec,
        ...(params.nextSector !== undefined ? { sector: params.nextSector } : {}),
        updatedBy: params.session.uid,
        draft: nextDraft,
        validation: {
          status: 'PENDING',
          validatedAt: nextDraft.submittedAt,
          errors: [],
          warnings: [],
        },
      };
      params.onLocalDraftUpdated(
        rec.identifierKey,
        nextDraft,
        params.session.uid,
        params.nextSector,
        stagedOnlyRecord,
      );
      params.onPendingPromotionStarted?.(
        rec.identifierKey,
        scheduled !== null ? promotedSuccessMessage : null,
      );
      params.setLinkFormSuccess('Checking…');
    }
    return true;
  } catch (err: unknown) {
    params.setLinkFormError(
      err instanceof Error ? err.message : 'Could not save your changes. Please try again.',
    );
    return false;
  } finally {
    params.setSavingDraft(false);
  }
}
